import type { HubspotFormProps } from "next-hubspot";
import { useHubspot, useHubspotResult } from "next-hubspot";
import { useDebugValue, useEffect, useMemo, useRef, useState } from "react";

export interface UseHubSpotFormResponse extends useHubspotResult {
  readonly isFormCreated: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
}

export const useHubspotForm = (formProps: HubspotFormProps): UseHubSpotFormResponse => {
  const [isFormCreated, setIsFormCreated] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const isError = !!error;

  const hubspotContext = useHubspot();
  const { hbspt } = hubspotContext;

  // ensure we only call hbspt.forms.create once per hook instance
  const hasCreatedRef = useRef(false);

  /**
   * Effect 1: call hbspt.forms.create once per hook instance,
   * when HubSpot is available on the client.
   */
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!hbspt || hasCreatedRef.current) return;

    hasCreatedRef.current = true;
    let cancelled = false;

    try {
      hbspt.forms.create(formProps);
    } catch (e) {
      console.error("Failed to create HubSpot form:", e);
      // surface the error asynchronously to avoid synchronous updates in the effect
      Promise.resolve().then(() => {
        if (!cancelled) {
          setError(e as Error);
        }
      });
    }

    return () => {
      cancelled = true;
    };
  }, [hbspt, formProps]);

  /**
   * Effect 2: observe the DOM for the HubSpot form iframe and wait for it to load.
   *
   * This works even if:
   * - HubSpot callbacks never fire
   * - the component is unmounted/remounted (e.g. Radix Tabs without forceMount)
   * - the form already exists when this hook mounts
   *
   * The form is considered loaded when:
   * - The iframe element exists inside the form frame
   * - The iframe has fired its load event OR has a height > 0
   */
  useEffect(() => {
    if (typeof window === "undefined") return;

    let cancelled = false;
    const selector = `.hs-form-frame[data-form-id="${formProps.formId}"]`;
    const iframeLoadHandlers = new Map<HTMLIFrameElement, () => void>();

    const checkIframeLoaded = (iframe: HTMLIFrameElement): boolean => {
      if (cancelled) return false;

      const formFrame = iframe.closest(selector);
      if (!formFrame) return false;

      const computedHeight = window.getComputedStyle(formFrame).height;
      const height = parseInt(computedHeight, 10);
      const hasHeight = height > 0;

      return hasHeight;
    };

    const handleIframeLoad = (iframe: HTMLIFrameElement) => {
      if (cancelled) return;

      if (checkIframeLoaded(iframe)) {
        setIsFormCreated(true);
        setError(null);

        const handler = iframeLoadHandlers.get(iframe);
        if (handler) {
          iframe.removeEventListener("load", handler);
          iframeLoadHandlers.delete(iframe);
        }
      }
    };

    const updateCreatedState = () => {
      if (cancelled) return;

      const formFrame = document.querySelector(selector) as HTMLElement | null;
      if (!formFrame) {
        setIsFormCreated(false);
        return;
      }

      const iframe = formFrame.querySelector("iframe") as HTMLIFrameElement | null;
      if (!iframe) {
        setIsFormCreated(false);
        return;
      }

      if (checkIframeLoaded(iframe)) {
        setIsFormCreated(true);
        setError(null);
        return;
      }

      if (!iframeLoadHandlers.has(iframe)) {
        const handler = () => handleIframeLoad(iframe);
        iframe.addEventListener("load", handler);
        iframeLoadHandlers.set(iframe, handler);
      }

      if (iframe.contentWindow) {
        try {
          if (iframe.contentWindow.document.readyState === "complete") {
            handleIframeLoad(iframe);
          }
        } catch {
          // Cross-origin iframe, can't access contentWindow
          // Rely on load event and height check
        }
      }
    };

    Promise.resolve().then(updateCreatedState);

    const root = (formProps.target && document.querySelector(formProps.target)) || document.body;

    if (!root) {
      return;
    }

    const observer = new MutationObserver(() => {
      updateCreatedState();
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"],
    });

    const intervalId = setInterval(() => {
      if (!cancelled) {
        updateCreatedState();
      }
    }, 100);

    return () => {
      cancelled = true;
      observer.disconnect();
      clearInterval(intervalId);

      iframeLoadHandlers.forEach((handler, iframe) => {
        iframe.removeEventListener("load", handler);
      });
      iframeLoadHandlers.clear();
    };
  }, [formProps.formId, formProps.target]);

  useDebugValue(`isFormCreated: ${String(isFormCreated)}`);
  useDebugValue(`isError: ${String(isError)}`);
  useDebugValue(`error: ${error}`);

  const value: UseHubSpotFormResponse = useMemo(
    () => ({
      ...hubspotContext,
      isFormCreated,
      isError,
      error,
    }),
    [hubspotContext, isFormCreated, isError, error],
  );

  return value;
};
